import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Trash2, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";
import { THERABODY_PRODUCTS } from "@/lib/constants";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  adminListProductOverrides,
  adminUpsertProductOverride,
  adminDeleteProductOverride,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const STOCK_CHOICES = [
  { value: "", label: "— No badge —" },
  { value: "in_stock", label: "In Stock" },
  { value: "limited", label: "Limited Stock" },
  { value: "backorder", label: "On Backorder" },
  { value: "out_of_stock", label: "Out of Stock" },
];

export default function AdminTherabody() {
  const nav = useNavigate();
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login", { replace: true }); return; }
    adminMe()
      .then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({
    onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await adminListProductOverrides();
      const byslug = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => { byslug[r.slug] = r; });
      setOverrides(byslug);
    } catch (err) {
      console.warn("overrides load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const formFor = (slug) => {
    if (drafts[slug] !== undefined) return drafts[slug];
    const o = overrides[slug] || {};
    return {
      stock_status: o.stock_status || "",
      stock_note: o.stock_note || "",
      how_to_video_url: o.how_to_video_url || "",
      care_guide: (o.care_guide || []).join("\n"),
    };
  };

  const setField = (slug, field, value) => {
    setDrafts((cur) => ({
      ...cur,
      [slug]: { ...formFor(slug), [field]: value },
    }));
  };

  const save = async (slug) => {
    const f = formFor(slug);
    setBusy(slug);
    try {
      const payload = {
        stock_status: f.stock_status || null,
        stock_note: f.stock_note?.trim() || null,
        how_to_video_url: f.how_to_video_url?.trim() || null,
        care_guide: f.care_guide
          ? f.care_guide.split("\n").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const updated = await adminUpsertProductOverride(slug, payload);
      setOverrides((cur) => ({ ...cur, [slug]: updated }));
      setDrafts((cur) => { const next = { ...cur }; delete next[slug]; return next; });
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy("");
    }
  };

  const removeOverride = async (slug) => {
    if (!window.confirm(`Clear all overrides for ${slug}?`)) return;
    setBusy(slug);
    try {
      await adminDeleteProductOverride(slug);
      setOverrides((cur) => { const next = { ...cur }; delete next[slug]; return next; });
      setDrafts((cur) => { const next = { ...cur }; delete next[slug]; return next; });
      toast.success("Cleared.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy("");
    }
  };

  const stocked = useMemo(
    () => Object.values(overrides).filter((o) => o.stock_status).length,
    [overrides]
  );

  return (
    <main data-testid="admin-therabody-page" className="bg-white min-h-screen">
      <SEO title="Therabody overrides" path="/admin/therabody" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Therabody overrides</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/therabody" target="_blank" rel="noopener noreferrer" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5" data-testid="open-public-therabody">
              <ExternalLink size={14} /> View catalogue
            </Link>
            <Link to="/admin/blog" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5" data-testid="back-to-blog">
              <ArrowLeft size={14} /> Blog console
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <p className="text-[#3C4048] mb-6 text-sm leading-relaxed">
          Set stock badges, attach a how-to video, and add care &amp; cleaning steps for each Therabody product.
          Empty fields fall back to the static catalogue. Currently <strong>{stocked}</strong> product{stocked === 1 ? "" : "s"} have stock badges.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[#5A5F6A]">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {THERABODY_PRODUCTS.map((p) => {
              const f = formFor(p.slug);
              const hasOverride = !!overrides[p.slug];
              return (
                <div key={p.slug} className="border border-[#e5e7eb] bg-white p-6" data-testid={`tb-row-${p.slug}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                    <h2 className="text-lg font-bold text-[#080808]">{p.name}</h2>
                    <span className="text-xs uppercase tracking-[0.18em] text-[#6B7280]">{p.slug}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="ash-label" htmlFor={`stock-${p.slug}`}>Stock status</label>
                      <select id={`stock-${p.slug}`} data-testid={`tb-stock-${p.slug}`} className="ash-input"
                        value={f.stock_status} onChange={(e) => setField(p.slug, "stock_status", e.target.value)}>
                        {STOCK_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="ash-label" htmlFor={`note-${p.slug}`}>Stock note (optional)</label>
                      <input id={`note-${p.slug}`} data-testid={`tb-note-${p.slug}`} className="ash-input"
                        placeholder="e.g. New shipment expected late March"
                        value={f.stock_note} onChange={(e) => setField(p.slug, "stock_note", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="ash-label" htmlFor={`video-${p.slug}`}>How-to video URL</label>
                      <input id={`video-${p.slug}`} data-testid={`tb-video-${p.slug}`} className="ash-input"
                        placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…"
                        value={f.how_to_video_url} onChange={(e) => setField(p.slug, "how_to_video_url", e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="ash-label" htmlFor={`care-${p.slug}`}>Care &amp; cleaning steps</label>
                      <textarea id={`care-${p.slug}`} data-testid={`tb-care-${p.slug}`} rows={4}
                        className="ash-input ash-textarea !min-h-[100px] font-mono text-xs"
                        placeholder="One step per line. Example:&#10;Wipe the head with a 70% isopropyl wipe after each use.&#10;Charge fully every 8 weeks even if unused."
                        value={f.care_guide} onChange={(e) => setField(p.slug, "care_guide", e.target.value)} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <button type="button" disabled={busy === p.slug} onClick={() => save(p.slug)} data-testid={`tb-save-${p.slug}`}
                      className="ash-btn-primary disabled:opacity-60">
                      {busy === p.slug ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Save</>}
                    </button>
                    {hasOverride && (
                      <button type="button" disabled={busy === p.slug} onClick={() => removeOverride(p.slug)} data-testid={`tb-delete-${p.slug}`}
                        className="text-xs text-[#991B1B] hover:underline inline-flex items-center gap-1.5">
                        <Trash2 size={12} /> Clear all overrides
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
